// REACT
import React from 'react';

// YARN
import { Box, ClickAwayListener, MenuItem, Select, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// SERVICES
import logoCover from 'assets/images/logo-cover.png';
import logoCoverTransparent from 'assets/images/logo-cover-transparent.png';
import logoCoverDarkGreen from 'assets/images/logo-cover-dark-green.png';
import { StyledTooltip } from 'utils/Datagrid/CustomGenericDataGridUtils';

// COMPONENTS
import Parcel from '../Result/Components/Parcel';
import ParcelDivision from '../Result/Components/ParcelDivision';

// INTERFACES
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { getFilteredCultures, getSeasonFilter } from '../Result/utils/utilsResult';

export const CustomSimulationCell = (params, editedSynthesisCellsRef, setSelectIndex) => {
  const { cultures } = params?.value;

  const isCellEdited = (idx) =>
    editedSynthesisCellsRef.current.includes(`${params?.id.toString()}-${params?.field}-${idx}`);

  const splitCultures = [];
  const normalCultures = [];

  cultures?.forEach((culture, idx) => {
    if (culture.isSplit) {
      splitCultures.push({ culture, index: idx });
    } else {
      normalCultures.push({ culture, index: idx });
    }
  });

  return (
    <React.Fragment>
      <div className={splitCultures.length > 0 ? 'MuiDataGrid-colCellSynthesis-division' : 'w100'}>
        {normalCultures.length > 0 && (
          <Parcel
            cultures={normalCultures.map((c) => c.culture)}
            indices={normalCultures.map((c) => c.index)}
            hasSplittedCulture={splitCultures.length > 0}
            isEdited={isCellEdited}
            onSelect={setSelectIndex}
          />
        )}

        {splitCultures.map(({ culture, index }) => (
          <ParcelDivision
            key={`division-${index}`}
            culture={culture}
            index={index}
            isEdited={isCellEdited(index)}
            onSelect={setSelectIndex}
          />
        ))}
      </div>
    </React.Fragment>
  );
};

const CultureSelect = ({
  cultureIndex,
  culture,
  culturesList,
  currentOrganicState,
  isNitrogenASelectedConstraint,
  onCultureChange,
  onCoverChange,
  seasonFilter,
}) => {
  const [open, setOpen] = React.useState(false);
  const cover = culture?.cover;
  const isCover = cover === 'POSSIBLE' || cover === 'LEGUME' || cover === 'OTHER';

  let filteredCulturesList = getFilteredCultures(seasonFilter, culturesList, currentOrganicState);

  return (
    <Box className="parcel-synthesis-select-row">
      <Select
        value={culture?.id}
        onChange={(e) => onCultureChange(e, cultureIndex)}
        className="parcel-synthesis-select"
        IconComponent={KeyboardArrowDownIcon}
        size="small"
      >
        {filteredCulturesList?.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </Select>
      {cover !== 'IMPOSSIBLE' && isCover && (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div className="wrapper-cover">
            <Tooltip
              id={`custom-tooltip`}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              open={open}
              onClose={() => setOpen(false)}
              title={
                <Box className="tooltip-cover-icon">
                  <StyledTooltip title="Couvert possible" placement="top">
                    <img
                      src={logoCoverTransparent}
                      alt="POSSIBLE"
                      className="dropletLogo"
                      onClick={() => {
                        onCoverChange('POSSIBLE', cultureIndex);
                        setOpen(false);
                      }}
                    />
                  </StyledTooltip>
                  {isNitrogenASelectedConstraint && (
                    <StyledTooltip title="Couvert à base légumineuse" placement="top">
                      <img
                        src={logoCover}
                        alt="LEGUME"
                        className="dropletLogo"
                        onClick={() => {
                          onCoverChange('LEGUME', cultureIndex);
                          setOpen(false);
                        }}
                      />
                    </StyledTooltip>
                  )}
                  <StyledTooltip title="Couvert à base non légumineuse" placement="top">
                    <img
                      src={logoCoverDarkGreen}
                      alt="OTHER"
                      className="dropletLogo"
                      onClick={() => {
                        onCoverChange('OTHER', cultureIndex);
                        setOpen(false);
                      }}
                    />
                  </StyledTooltip>
                </Box>
              }
              placement="bottom"
            >
              <Box className="wrapper-cover-icon">
                <img
                  src={
                    cover === 'POSSIBLE'
                      ? logoCoverTransparent
                      : cover === 'LEGUME'
                        ? logoCover
                        : cover === 'OTHER'
                          ? logoCoverDarkGreen
                          : null
                  }
                  alt="Logo couvert"
                  className="dropletLogo"
                  onClick={() => setOpen((prev) => !prev)}
                />
              </Box>
            </Tooltip>
          </div>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export const CustomEditSelectCell = ({
  params,
  culturesList,
  selectIndex,
  isNitrogenASelectedConstraint,
}) => {
  const { id, field, value } = params;
  const { cultures } = value;
  const apiRef = useGridApiContext();

  const currentOrganicState = params.row.organicState;
  const selectedCulture = cultures[selectIndex];
  const isSelectedSplit = selectedCulture?.isSplit;

  const unsplitCulturesWithIndex = cultures
    .map((c, idx) => ({ culture: c, index: idx }))
    .filter((item) => !item.culture.isSplit);

  const handleCultureChange = (e, cultureIndex) => {
    const updatedValueObject = structuredClone(params.value);

    const newValue = {
      id: e.target.value,
      name: culturesList.find((c) => c.id === Number(e.target.value))?.name,
      surface: cultures[cultureIndex]?.surface,
      irrigation: cultures[cultureIndex]?.irrigation,
      isSplit: cultures[cultureIndex]?.isSplit,
      cover: cultures[cultureIndex]?.cover,
      customizedFor: cultures[cultureIndex]?.customizedFor,
      season: cultures[cultureIndex]?.season,
      cellId: `${id.toString()}-${field}-${cultureIndex}`,
      isEdited: true,
    };

    updatedValueObject.cultures[cultureIndex] = newValue;
    apiRef.current.setEditCellValue({ id, field, value: updatedValueObject });

    if (isSelectedSplit || unsplitCulturesWithIndex.length === 1) {
      apiRef.current.stopCellEditMode({ id, field });
    }
  };

  const handleCoverChange = (newCover, cultureIndex) => {
    const newValue = {
      ...value,
      cultures: value.cultures.map((culture, index) => {
        if (index === cultureIndex) {
          return {
            ...culture,
            cover: newCover,
            cellId: `${id.toString()}-${field}-${cultureIndex}`,
            isEdited: true,
          };
        }
        return culture;
      }),
    };
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  const culturesToEdit = isSelectedSplit
    ? [{ culture: selectedCulture, index: selectIndex }]
    : unsplitCulturesWithIndex;

  const isMultiple = culturesToEdit.length > 1;

  return (
    <Box
      className={`parcel-synthesis-select-wrapper ${
        isMultiple ? 'parcel-synthesis-select-wrapper--multiple' : ''
      }`}
    >
      <div className={isMultiple ? 'parcel-synthesis-select-list' : undefined}>
        {culturesToEdit.map(({ culture, index }, positionInList) => (
          <CultureSelect
            key={index}
            cultureIndex={index}
            culture={culture}
            culturesList={culturesList}
            currentOrganicState={currentOrganicState}
            isNitrogenASelectedConstraint={isNitrogenASelectedConstraint}
            onCultureChange={handleCultureChange}
            onCoverChange={handleCoverChange}
            seasonFilter={getSeasonFilter(positionInList, culturesToEdit.length)}
          />
        ))}
      </div>
    </Box>
  );
};
